import React, { useState, useEffect, useRef } from 'react'
import { api } from '../api/client'
import { useToast } from '../context/useToast'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import SectionTitle from '../components/ui/SectionTitle'
import { PrimaryButton, SecondaryButton } from '../components/ui/Button'

const TARGET_LUFS = -14

const PROFILE_LABELS = {
  spotify_safe: 'Spotify Safe',
  spotify_perfect: 'Spotify Perfect Master',
  spotify_optimized: 'Spotify Optimized',
  club_loud: 'Club (Legacy)',
  club: 'Club (Legacy)',
  club_punch: 'Club Punch',
  club_modern: 'Club Modern',
  general: 'Spotify Safe',
  radio: 'Radio',
  festival: 'Festival',
}

function buildTempTrack(file, durationSeconds, analysisResult = null) {
  const d = analysisResult?.duration_seconds ?? durationSeconds
  const lufs = analysisResult?.lufs_integrated ?? null
  const tp = analysisResult?.true_peak_db ?? null
  return {
    title_display: file.name,
    original_filename: file.name,
    analysis: d != null ? { duration_seconds: d } : {},
    render: (lufs != null || tp != null) ? { qc_lufs: lufs, qc_true_peak: tp } : {},
    _temp: true,
  }
}

function buildComparison(trackA, trackB) {
  if (!trackA || !trackB) return ''
  const a = trackA.render || {}
  const b = trackB.render || {}
  const lufsA = a.qc_lufs != null ? Number(a.qc_lufs) : null
  const lufsB = b.qc_lufs != null ? Number(b.qc_lufs) : null
  const peakA = a.qc_true_peak != null ? Number(a.qc_true_peak) : null
  const peakB = b.qc_true_peak != null ? Number(b.qc_true_peak) : null
  const nameA = trackA.title_display || trackA.original_filename || 'Track A'
  const nameB = trackB.title_display || trackB.original_filename || 'Track B'

  const parts = []
  if (lufsA != null && lufsB != null) {
    const diffA = Math.abs(lufsA - TARGET_LUFS)
    const diffB = Math.abs(lufsB - TARGET_LUFS)
    if (Math.abs(diffA - diffB) < 0.2) {
      parts.push(`Beide Masters liegen nah am Streaming-Ziel (${TARGET_LUFS} LUFS): „${nameA}“ bei ${lufsA} LUFS, „${nameB}“ bei ${lufsB} LUFS. Für Spotify/Streaming sind beide gut geeignet.`)
    } else if (diffA < diffB) {
      parts.push(`„${nameA}“ ist näher am typischen Streaming-Ziel (${TARGET_LUFS} LUFS) mit ${lufsA} LUFS. „${nameB}“ liegt bei ${lufsB} LUFS und ist damit ${lufsB < lufsA ? 'leiser' : 'lauter'}. Für einheitliche Playlist-Lautheit eignet sich „${nameA}“ etwas besser.`)
    } else {
      parts.push(`„${nameB}“ ist näher am typischen Streaming-Ziel (${TARGET_LUFS} LUFS) mit ${lufsB} LUFS. „${nameA}“ liegt bei ${lufsA} LUFS. Für einheitliche Lautheit in einer Playlist eignet sich „${nameB}“ etwas besser.`)
    }
  }
  if (peakA != null && peakB != null) {
    if (parts.length) parts.push(' ')
    if (peakA > peakB) {
      parts.push(`„${nameB}“ hat den niedrigeren True Peak (${peakB} dB vs. ${peakA} dB) und damit mehr Headroom; „${nameA}“ ist näher an der Begrenzung.`)
    } else if (peakB > peakA) {
      parts.push(`„${nameA}“ hat den niedrigeren True Peak (${peakA} dB vs. ${peakB} dB) und damit mehr Headroom.`)
    }
  }
  const ratingA = trackA.rating != null ? Number(trackA.rating) : null
  const ratingB = trackB.rating != null ? Number(trackB.rating) : null
  if (ratingA != null && ratingB != null && parts.length > 0) {
    parts.push(' ')
    if (ratingA > ratingB) {
      parts.push(`Deine Bewertung: „${nameA}“ (${ratingA}/10) liegt über „${nameB}“ (${ratingB}/10).`)
    } else if (ratingB > ratingA) {
      parts.push(`Deine Bewertung: „${nameB}“ (${ratingB}/10) liegt über „${nameA}“ (${ratingA}/10).`)
    } else {
      parts.push(`Beide mit Bewertung ${ratingA}/10.`)
    }
  }
  if (parts.length === 0) {
    const hasLufs = (trackA.render?.qc_lufs != null || trackB.render?.qc_lufs != null)
    if (hasLufs) return 'Weitere Messwerte hinzufügen (z. B. zweites Master analysieren), um die Bewertung zu sehen.'
    return 'Wähle zwei Masters und ggf. „Messwerte ermitteln“ bei temporären Dateien. Die Bewertung basiert auf LUFS (Lautheit) und True Peak (Spitzen).'
  }
  return parts.join('')
}

function useTempFileDuration(file) {
  const [duration, setDuration] = useState(null)
  const urlRef = useRef(null)

  useEffect(() => {
    if (!file) {
      setDuration(null)
      return
    }
    const url = URL.createObjectURL(file)
    urlRef.current = url
    const audio = new Audio()
    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration)
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
    })
    audio.addEventListener('error', () => {
      setDuration(null)
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
    })
    audio.src = url
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
    }
  }, [file])

  return duration
}

export default function Compare() {
  const toast = useToast()
  const [tracks, setTracks] = useState([])
  const [details, setDetails] = useState({})
  const [loading, setLoading] = useState(true)
  const [idA, setIdA] = useState('')
  const [idB, setIdB] = useState('')
  const [tempFileA, setTempFileA] = useState(null)
  const [tempFileB, setTempFileB] = useState(null)
  const [tempAnalysisA, setTempAnalysisA] = useState(null)
  const [tempAnalysisB, setTempAnalysisB] = useState(null)
  const [analyzingA, setAnalyzingA] = useState(false)
  const [analyzingB, setAnalyzingB] = useState(false)
  const fileInputARef = useRef(null)
  const fileInputBRef = useRef(null)

  const tempDurationA = useTempFileDuration(tempFileA)
  const tempDurationB = useTempFileDuration(tempFileB)

  useEffect(() => {
    api.get('/tracks')
      .then((r) => setTracks(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setTracks([]))
      .finally(() => setLoading(false))
  }, [])

  const completed = tracks.filter((t) => t.status === 'completed')
  const trackA = tempFileA
    ? buildTempTrack(tempFileA, tempDurationA, tempAnalysisA)
    : (idA ? details[idA] || completed.find((t) => String(t.id) === idA) : null)
  const trackB = tempFileB
    ? buildTempTrack(tempFileB, tempDurationB, tempAnalysisB)
    : (idB ? details[idB] || completed.find((t) => String(t.id) === idB) : null)

  useEffect(() => {
    if (idA && !details[idA]) {
      api.get(`/tracks/${idA}`).then((r) => setDetails((d) => ({ ...d, [idA]: r.data }))).catch(() => {})
    }
    if (idB && !details[idB]) {
      api.get(`/tracks/${idB}`).then((r) => setDetails((d) => ({ ...d, [idB]: r.data }))).catch(() => {})
    }
  }, [idA, idB, details])

  const analyzeTempFile = async (file, setAnalyzing, setResult) => {
    setAnalyzing(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post('/compare/analyze', form)
      setResult(data)
      toast('Messwerte ermittelt', 'success')
    } catch (e) {
      toast(e?.message || 'Analyse fehlgeschlagen', 'error')
    } finally {
      setAnalyzing(false)
    }
  }

  const onTempFileA = (e) => {
    const f = e.target.files?.[0]
    if (f) {
      setTempFileA(f)
      setTempAnalysisA(null)
      setIdA('')
    }
    e.target.value = ''
  }
  const onTempFileB = (e) => {
    const f = e.target.files?.[0]
    if (f) {
      setTempFileB(f)
      setTempAnalysisB(null)
      setIdB('')
    }
    e.target.value = ''
  }
  const clearTempA = () => {
    setTempFileA(null)
    setTempAnalysisA(null)
    if (fileInputARef.current) fileInputARef.current.value = ''
  }
  const clearTempB = () => {
    setTempFileB(null)
    setTempAnalysisB(null)
    if (fileInputBRef.current) fileInputBRef.current.value = ''
  }

  const canCompare = trackA && trackB && (idA !== idB || tempFileA || tempFileB)
  const comparisonText = buildComparison(trackA, trackB)

  const nameA = trackA?.title_display || trackA?.original_filename || 'Track 1'
  const nameB = trackB?.title_display || trackB?.original_filename || 'Track 2'
  const profileA = trackA?.mastering_plan?.target_profile
  const profileB = trackB?.mastering_plan?.target_profile

  return (
    <PageShell>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Master-Vergleich</h1>
          <p className="mt-2 text-white/70">
            Zwei Masters auswählen – aus deinen gespeicherten Tracks oder temporär hochgeladene Dateien. Du siehst Messwerte und eine Bewertung. Temporäre Dateien werden nur im Browser verwendet und nicht gespeichert.
          </p>
        </div>

        {loading ? (
          <p className="text-white/70">Laden…</p>
        ) : (
          <>
            <Card>
              <SectionTitle className="mb-4">Tracks auswählen</SectionTitle>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="compare-track-a" className="block text-sm font-medium text-white/80">Track 1</label>
                  {tempFileA ? (
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                      <span className="min-w-0 flex-1 truncate text-sm text-white/90" title={tempFileA.name}>Temporär: {tempFileA.name}</span>
                      <PrimaryButton
                        onClick={() => analyzeTempFile(tempFileA, setAnalyzingA, setTempAnalysisA)}
                        disabled={analyzingA}
                      >
                        {analyzingA ? 'Analysiere…' : 'Messwerte ermitteln'}
                      </PrimaryButton>
                      <SecondaryButton onClick={clearTempA}>Entfernen</SecondaryButton>
                    </div>
                  ) : (
                    <>
                      <select
                        id="compare-track-a"
                        value={idA}
                        onChange={(e) => setIdA(e.target.value)}
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      >
                        <option value="">– auswählen –</option>
                        {completed.map((t) => (
                          <option key={t.id} value={t.id}>{t.title_display || t.original_filename}</option>
                        ))}
                      </select>
                      <p className="text-xs text-white/50">Oder temporär:</p>
                      <input
                        ref={fileInputARef}
                        id="compare-temp-a"
                        type="file"
                        accept="audio/*"
                        onChange={onTempFileA}
                        className="block w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-violet-600 file:px-4 file:py-2 file:text-white file:hover:bg-violet-500"
                      />
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  <label htmlFor="compare-track-b" className="block text-sm font-medium text-white/80">Track 2</label>
                  {tempFileB ? (
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                      <span className="min-w-0 flex-1 truncate text-sm text-white/90" title={tempFileB.name}>Temporär: {tempFileB.name}</span>
                      <PrimaryButton
                        onClick={() => analyzeTempFile(tempFileB, setAnalyzingB, setTempAnalysisB)}
                        disabled={analyzingB}
                      >
                        {analyzingB ? 'Analysiere…' : 'Messwerte ermitteln'}
                      </PrimaryButton>
                      <SecondaryButton onClick={clearTempB}>Entfernen</SecondaryButton>
                    </div>
                  ) : (
                    <>
                      <select
                        id="compare-track-b"
                        value={idB}
                        onChange={(e) => setIdB(e.target.value)}
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      >
                        <option value="">– auswählen –</option>
                        {completed.map((t) => (
                          <option key={t.id} value={t.id}>{t.title_display || t.original_filename}</option>
                        ))}
                      </select>
                      <p className="text-xs text-white/50">Oder temporär:</p>
                      <input
                        ref={fileInputBRef}
                        id="compare-temp-b"
                        type="file"
                        accept="audio/*"
                        onChange={onTempFileB}
                        className="block w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-violet-600 file:px-4 file:py-2 file:text-white file:hover:bg-violet-500"
                      />
                    </>
                  )}
                </div>
              </div>
            </Card>

            {canCompare && (
              <>
                <Card>
                  <SectionTitle className="mb-4">Messwerte</SectionTitle>
                  <div className="overflow-x-auto rounded-xl border border-white/10">
                    <table className="w-full min-w-[400px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="bg-white/5 px-4 py-3 text-left font-medium text-white/50"></th>
                          <th className="bg-white/5 px-4 py-3 text-left font-medium text-white/90">{nameA}</th>
                          <th className="bg-white/5 px-4 py-3 text-left font-medium text-white/90">{nameB}</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/90">
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">LUFS</td>
                          <td className="px-4 py-3">{trackA?.render?.qc_lufs != null ? trackA.render.qc_lufs : '–'}</td>
                          <td className="px-4 py-3">{trackB?.render?.qc_lufs != null ? trackB.render.qc_lufs : '–'}</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">True Peak</td>
                          <td className="px-4 py-3">{trackA?.render?.qc_true_peak != null ? `${trackA.render.qc_true_peak} dB` : '–'}</td>
                          <td className="px-4 py-3">{trackB?.render?.qc_true_peak != null ? `${trackB.render.qc_true_peak} dB` : '–'}</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">Dauer</td>
                          <td className="px-4 py-3">{trackA?.analysis?.duration_seconds != null ? `${Number(trackA.analysis.duration_seconds).toFixed(1)} s` : '–'}</td>
                          <td className="px-4 py-3">{trackB?.analysis?.duration_seconds != null ? `${Number(trackB.analysis.duration_seconds).toFixed(1)} s` : '–'}</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">Profil</td>
                          <td className="px-4 py-3">{PROFILE_LABELS[profileA] || profileA || '–'}</td>
                          <td className="px-4 py-3">{PROFILE_LABELS[profileB] || profileB || '–'}</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">Genre</td>
                          <td className="px-4 py-3">{trackA?.genre || '–'}</td>
                          <td className="px-4 py-3">{trackB?.genre || '–'}</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="px-4 py-3 text-white/50">Explicit</td>
                          <td className="px-4 py-3">{trackA?.is_explicit ? 'Ja' : '–'}</td>
                          <td className="px-4 py-3">{trackB?.is_explicit ? 'Ja' : '–'}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-white/50">Bewertung (1–10)</td>
                          <td className="px-4 py-3">{trackA?.rating != null ? trackA.rating : '–'}</td>
                          <td className="px-4 py-3">{trackB?.rating != null ? trackB.rating : '–'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card>
                  <SectionTitle className="mb-4">Vergleich & Bewertung</SectionTitle>
                  <p className="whitespace-pre-line leading-relaxed text-white/80">{comparisonText}</p>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </PageShell>
  )
}
